import React, { useState, useEffect } from 'react';
import { auth } from '../firebase';
import { listPulse, listSuggestedUsers, listTopCreators } from '../utils/secureFunctions';
import { useNavigate } from 'react-router-dom';
import AdSlot from './AdSlot';
import ImageWithFallback from './ImageWithFallback';
import { getProfileUrl } from '../utils/urls';
import UserLink from './UserLink';
import '../styles/sidebar.css';

interface TrendingCreator {
  uid: string;
  name: string;
  username: string;
  photoURL: string;
  creatorScore: number;
  followers: number;
}

interface SuggestedUser {
  uid: string;
  name: string;
  username: string;
  photoURL: string;
}

interface TrendingGroup {
  id: string;
  name: string;
  memberCount: number;
  coverImage?: string;
  category?: string;
}

interface TrendingVideo {
  id: string;
  userId: string;
  userName: string;
  videoURL: string;
  views: number;
  likesCount: number;
}

const RightWidgetsSidebar: React.FC = () => {
  const [trendingCreators, setTrendingCreators] = useState<TrendingCreator[]>([]);
  const [suggestedUsers, setSuggestedUsers] = useState<SuggestedUser[]>([]);
  const [trendingGroups, setTrendingGroups] = useState<TrendingGroup[]>([]);
  const [trendingVideos, setTrendingVideos] = useState<TrendingVideo[]>([]);
  const [trendMode, setTrendMode] = useState<'creators' | 'groups' | 'videos'>('creators');
  const navigate = useNavigate();

  useEffect(() => {
    loadTrendingCreators();
    loadSuggestedUsers();
    setTrendingGroups([]);
    loadTrendingVideos();
  }, []);

  // Rotate headline/mode every 8 seconds among the available datasets
  useEffect(() => {
    const available: Array<'creators'|'groups'|'videos'> = [];
    if (trendingCreators.length) available.push('creators');
    if (trendingGroups.length) available.push('groups');
    if (trendingVideos.length) available.push('videos');
    if (available.length <= 1) return; // Nothing to rotate

    let i = available.indexOf(trendMode);
    const t = setInterval(() => {
      i = (i + 1) % available.length;
      setTrendMode(available[i]);
    }, 8000);
    return () => clearInterval(t);
  }, [trendingCreators.length, trendingGroups.length, trendingVideos.length, trendMode]);

  const loadTrendingCreators = async () => {
    try {
      const res = await listTopCreators({ limit: 3 });
      setTrendingCreators(res.users.map((u) => ({
        uid: u.uid,
        name: u.name || 'User',
        username: u.username || 'user',
        photoURL: u.photoURL || '',
        creatorScore: u.creatorScore || 0,
        followers: u.followers || 0,
      })));
    } catch (e) {
      console.warn('Failed to load trending creators', e);
    }
  };

  const loadSuggestedUsers = async () => {
    if (!auth.currentUser) return;
    try {
      const res = await listSuggestedUsers({ limit: 6 });
      const filtered = res.users.filter((u) => u.uid !== auth.currentUser!.uid);
      setSuggestedUsers(filtered.slice(0, 3).map((u) => ({
        uid: u.uid,
        name: u.name || 'User',
        username: u.username || 'user',
        photoURL: u.photoURL || '',
      })));
    } catch (e) {
      console.warn('Failed to load suggested users', e);
    }
  };

  const loadTrendingVideos = async () => {
    try {
      const res = await listPulse({ limit: 3, mode: 'forYou' });
      const items = res.pulses.map((p) => ({
        id: p.id,
        userId: p.userId || '',
        userName: p.userName || 'Creator',
        videoURL: p.videoURL || '',
        views: p.views || 0,
        likesCount: p.likeCount || 0,
      }));
      setTrendingVideos(items);
    } catch (e) {
      console.warn('Failed to load trending videos', e);
    }
  };

  return (
    <aside className="right-widgets-sidebar" style={styles.sidebar}>
      {/* Sponsored Ad */}
      <div style={styles.widget}>
        <h3 style={styles.widgetTitle}>💡 Sponsored</h3>
        <AdSlot placement="sidebar" />
      </div>

      {/* Trending Dynamic Widget */}
      <div style={styles.widget}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={styles.widgetTitle}>
            {trendMode === 'creators' && '🔥 Trending Creators'}
            {trendMode === 'groups' && '💬 Trending Groups'}
            {trendMode === 'videos' && '🎬 Trending Videos'}
          </h3>
          <div style={{ display: 'flex', gap: 6 }}>
            <button style={styles.pillBtn} onClick={() => setTrendMode('creators')}>Creators</button>
            <button style={styles.pillBtn} onClick={() => setTrendMode('groups')}>Groups</button>
            <button style={styles.pillBtn} onClick={() => setTrendMode('videos')}>Videos</button>
          </div>
        </div>

        {trendMode === 'creators' && (
          trendingCreators.length === 0 ? (
            <p style={styles.emptyText}>No trending creators yet</p>
          ) : (
            trendingCreators.map((creator) => (
              <div
                key={creator.uid}
                style={styles.userItem}
                onClick={() => navigate(getProfileUrl({ id: creator.uid, username: (creator as any).username }))}
              >
                <ImageWithFallback
                  src={creator.photoURL || undefined}
                  alt=""
                  style={styles.userAvatar}
                />
                <div style={styles.userInfo}>
                  <div style={styles.userName}>{creator.name}</div>
                  <div style={styles.userMeta}>
                    @{creator.username} • {creator.followers} followers
                  </div>
                </div>
              </div>
            ))
          )
        )}

        {trendMode === 'groups' && (
          trendingGroups.length === 0 ? (
            <p style={styles.emptyText}>No trending groups yet</p>
          ) : (
            trendingGroups.map((g) => (
              <div
                key={g.id}
                style={styles.userItem}
                onClick={() => navigate('/groups')}
              >
                <ImageWithFallback src={g.coverImage} alt="" style={styles.userAvatar} />
                <div style={styles.userInfo}>
                  <div style={styles.userName}>{g.name}</div>
                  <div style={styles.userMeta}>{g.memberCount} members{g.category ? ` • ${g.category}` : ''}</div>
                </div>
              </div>
            ))
          )
        )}

        {trendMode === 'videos' && (
          trendingVideos.length === 0 ? (
            <p style={styles.emptyText}>No trending videos yet</p>
          ) : (
            trendingVideos.map((v) => (
              <div
                key={v.id}
                style={styles.userItem}
                onClick={() => navigate('/pulse')}
              >
                <div style={{ ...styles.userAvatar, backgroundColor: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ color: '#fff', fontSize: 16 }}>▶</span>
                </div>
                <div style={styles.userInfo}>
                  <div style={styles.userName}><UserLink userId={v.userId}>{v.userName}</UserLink></div>
                  <div style={styles.userMeta}>{v.views} views • {v.likesCount} likes</div>
                </div>
              </div>
            ))
          )
        )}
      </div>

      {/* People to Follow */}
      <div style={styles.widget}>
        <h3 style={styles.widgetTitle}>👥 People to Follow</h3>
        {suggestedUsers.length === 0 ? (
          <p style={styles.emptyText}>No suggestions yet</p>
        ) : (
          suggestedUsers.map((user) => (
            <div
              key={user.uid}
              style={styles.userItem}
              onClick={() => navigate(getProfileUrl({ id: user.uid, username: (user as any).username }))}
            >
              <ImageWithFallback
                src={user.photoURL || undefined}
                alt=""
                style={styles.userAvatar}
              />
              <div style={styles.userInfo}>
                <div style={styles.userName}>{user.name}</div>
                <div style={styles.userMeta}>@{user.username}</div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Footer Links */}
      <div style={styles.footer}>
        <button style={styles.footerLinkButton} onClick={() => navigate('/about')}>About</button>
        <span style={styles.footerDot}>•</span>
        <button style={styles.footerLinkButton} onClick={() => navigate('/help')}>Help</button>
        <span style={styles.footerDot}>•</span>
        <button style={styles.footerLinkButton} onClick={() => navigate('/privacy')}>Privacy</button>
        <span style={styles.footerDot}>•</span>
        <button style={styles.footerLinkButton} onClick={() => navigate('/terms')}>Terms</button>
        <div style={styles.copyright}>© {new Date().getFullYear()} Belivah</div>
      </div>
    </aside>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  sidebar: {
    width: '320px',
    height: 'calc(100vh - 64px)',
    position: 'fixed',
    right: 0,
    top: 64,
    backgroundColor: 'var(--color-background-alt)',
    overflowY: 'auto',
    padding: 'var(--spacing-base) var(--spacing-md)',
    zIndex: 100,
  },
  widget: {
    backgroundColor: 'var(--color-surface)',
    borderRadius: 'var(--radius-lg)',
    padding: 'var(--spacing-base)',
    marginBottom: 'var(--spacing-base)',
    boxShadow: 'var(--shadow-sm)',
    border: '1px solid var(--color-border)',
    transition: 'box-shadow var(--transition-base)',
  },
  pillBtn: {
    padding: '6px 10px',
    fontSize: '12px',
    borderRadius: '999px',
    border: '1px solid var(--color-border)',
    background: 'var(--color-background)',
    color: 'var(--color-text-primary)',
    cursor: 'pointer',
  },
  widgetTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: 'var(--color-text-primary)',
    marginBottom: 'var(--spacing-md)',
    marginTop: 0,
  },
  userItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: 'var(--spacing-sm)',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
    transition: 'background-color var(--transition-fast)',
    marginBottom: 'var(--spacing-xs)',
  },
  userAvatar: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    objectFit: 'cover' as const,
    border: '2px solid var(--color-border-light)',
  },
  userInfo: {
    flex: 1,
    minWidth: 0,
  },
  userName: {
    fontSize: '14px',
    fontWeight: '500',
    color: 'var(--color-text-primary)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  userMeta: {
    fontSize: '12px',
    color: 'var(--color-text-primary)',
  },
  emptyText: {
    fontSize: '13px',
    color: 'var(--color-text-primary)',
    textAlign: 'center' as const,
    padding: '20px 0',
    margin: 0,
  },
  footer: {
    padding: 'var(--spacing-md) var(--spacing-sm) 32px',
    marginTop: 'var(--spacing-sm)',
    fontSize: '12px',
    color: 'var(--color-text-primary)',
    textAlign: 'center' as const,
    lineHeight: '1.6',
  },
  footerLink: {
    color: 'var(--color-text-secondary)',
    textDecoration: 'none',
    margin: '0 4px',
    transition: 'color var(--transition-fast)',
  },
  footerDot: {
    margin: '0 4px',
  },
  copyright: {
    marginTop: 'var(--spacing-sm)',
    fontSize: '11px',
  },
  footerLinkButton: {
    color: 'var(--color-text-primary)',
    textDecoration: 'none',
    background: 'transparent',
    border: 'none',
    padding: '2px 4px',
    fontSize: '13px',
    fontWeight: 500,
    minHeight: 0,
    minWidth: 0,
    cursor: 'pointer'
  },
};

export default RightWidgetsSidebar;
